import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export const TenantSetupWizard = () => {
  const [step, setStep] = useState(1);
  const [tenantData, setTenantData] = useState({
    name: '',
    code: '',
    max_markers_per_textbook: 20,
  });
  const [boards, setBoards] = useState([{ name: '', code: '' }]);
  const [institutions, setInstitutions] = useState([{ name: '', code: '', address: '' }]);
  const [classes, setClasses] = useState([
    { name: 'Class 6', code: 'CLASS_6' },
    { name: 'Class 7', code: 'CLASS_7' },
    { name: 'Class 8', code: 'CLASS_8' },
    { name: 'Class 9', code: 'CLASS_9' },
    { name: 'Class 10', code: 'CLASS_10' },
    { name: 'Class 11', code: 'CLASS_11' },
    { name: 'Class 12', code: 'CLASS_12' },
  ]);
  const [subjects, setSubjects] = useState([
    { name: 'Mathematics', code: 'MATH', classes: ['CLASS_6', 'CLASS_7', 'CLASS_8', 'CLASS_9', 'CLASS_10', 'CLASS_11', 'CLASS_12'] },
    { name: 'Science', code: 'SCI', classes: ['CLASS_6', 'CLASS_7', 'CLASS_8', 'CLASS_9', 'CLASS_10'] },
    { name: 'Physics', code: 'PHY', classes: ['CLASS_11', 'CLASS_12'] },
    { name: 'Chemistry', code: 'CHEM', classes: ['CLASS_11', 'CLASS_12'] },
    { name: 'Biology', code: 'BIO', classes: ['CLASS_11', 'CLASS_12'] },
  ]);
  const [loading, setLoading] = useState(false);

  const addBoard = () => setBoards([...boards, { name: '', code: '' }]);
  const removeBoard = (index) => setBoards(boards.filter((_, i) => i !== index));
  const updateBoard = (index, field, value) => {
    const updated = [...boards];
    updated[index][field] = value;
    setBoards(updated);
  };

  const addInstitution = () => setInstitutions([...institutions, { name: '', code: '', address: '' }]);
  const removeInstitution = (index) => setInstitutions(institutions.filter((_, i) => i !== index));
  const updateInstitution = (index, field, value) => {
    const updated = [...institutions];
    updated[index][field] = value;
    setInstitutions(updated);
  };

  const addClass = () => setClasses([...classes, { name: '', code: '' }]);
  const removeClass = (index) => setClasses(classes.filter((_, i) => i !== index));
  const updateClass = (index, field, value) => {
    const updated = [...classes];
    updated[index][field] = value;
    setClasses(updated);
  };

  const addSubject = () => setSubjects([...subjects, { name: '', code: '', classes: [] }]);
  const removeSubject = (index) => setSubjects(subjects.filter((_, i) => i !== index));
  const updateSubject = (index, field, value) => {
    const updated = [...subjects];
    updated[index][field] = value;
    setSubjects(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Step 1: Create Tenant
      const tenantRes = await api.post('/tenants', tenantData);
      const tenantId = tenantRes.data.id;
      toast.success('Tenant created');

      // Step 2: Create Boards
      const boardIds = {};
      for (const board of boards) {
        if (board.name && board.code) {
          const res = await api.post('/boards', {
            ...board,
            tenant_id: tenantId,
          });
          boardIds[board.code] = res.data.id;
        }
      }
      toast.success(`${Object.keys(boardIds).length} boards created`);

      // Step 3: Create Classes
      const classIds = {};
      for (const cls of classes) {
        if (cls.name && cls.code) {
          const res = await api.post('/classes', cls);
          classIds[cls.code] = res.data.id;
        }
      }
      toast.success(`${Object.keys(classIds).length} classes created`);

      // Step 4: Create Subjects
      for (const subject of subjects) {
        if (subject.name && subject.code) {
          for (const classCode of subject.classes) {
            if (classIds[classCode]) {
              await api.post('/subjects', {
                name: subject.name,
                code: subject.code,
                class_id: classIds[classCode],
              });
            }
          }
        }
      }
      toast.success('Subjects created for all classes');

      // Step 5: Create Institutions (tracked for future use)
      for (const inst of institutions) {
        if (inst.name && inst.code) {
          // Store institution metadata (for future user assignments)
          console.log('Institution ready:', inst);
        }
      }

      toast.success('✅ Complete tenant setup successful!');
      window.location.href = '/admin/super';
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Tenant Setup Wizard">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-indigo-900 mb-2">Complete Tenant Setup</h1>
          <p className="text-slate-600">Step {step} of 5: Set up a complete multi-tenant deployment</p>
        </div>

        <div className="flex justify-between mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`flex-1 h-2 mx-1 rounded ${s <= step ? 'bg-indigo-600' : 'bg-slate-200'}`}
            />
          ))}
        </div>

        {step === 1 && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-indigo-900">Step 1: Tenant Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tenant Name *</Label>
                <Input
                  value={tenantData.name}
                  onChange={(e) => setTenantData({ ...tenantData, name: e.target.value })}
                  placeholder="e.g., Jharkhand State"
                  className="border-slate-300"
                />
              </div>
              <div>
                <Label>Tenant Code *</Label>
                <Input
                  value={tenantData.code}
                  onChange={(e) => setTenantData({ ...tenantData, code: e.target.value })}
                  placeholder="e.g., JAC"
                  className="border-slate-300"
                />
              </div>
              <div>
                <Label>Max Markers Per Textbook</Label>
                <Input
                  type="number"
                  value={tenantData.max_markers_per_textbook}
                  onChange={(e) => setTenantData({ ...tenantData, max_markers_per_textbook: parseInt(e.target.value) })}
                  className="border-slate-300"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-indigo-900">Step 2: Boards</CardTitle>
                <Button onClick={addBoard} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Board
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {boards.map((board, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Board Name</Label>
                    <Input
                      value={board.name}
                      onChange={(e) => updateBoard(index, 'name', e.target.value)}
                      placeholder="e.g., CBSE"
                      className="border-slate-300"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Board Code</Label>
                    <Input
                      value={board.code}
                      onChange={(e) => updateBoard(index, 'code', e.target.value)}
                      placeholder="e.g., CBSE"
                      className="border-slate-300"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBoard(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-indigo-900">Step 3: Classes</CardTitle>
                <Button onClick={addClass} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Class
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {classes.map((cls, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Class Name</Label>
                    <Input
                      value={cls.name}
                      onChange={(e) => updateClass(index, 'name', e.target.value)}
                      placeholder="e.g., Class 6"
                      className="border-slate-300"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Class Code</Label>
                    <Input
                      value={cls.code}
                      onChange={(e) => updateClass(index, 'code', e.target.value)}
                      placeholder="e.g., CLASS_6"
                      className="border-slate-300"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeClass(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-indigo-900">Step 4: Subjects</CardTitle>
                <Button onClick={addSubject} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Subject
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {subjects.map((subject, index) => (
                <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label>Subject Name</Label>
                      <Input
                        value={subject.name}
                        onChange={(e) => updateSubject(index, 'name', e.target.value)}
                        placeholder="e.g., Mathematics"
                        className="border-slate-300"
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Subject Code</Label>
                      <Input
                        value={subject.code}
                        onChange={(e) => updateSubject(index, 'code', e.target.value)}
                        placeholder="e.g., MATH"
                        className="border-slate-300"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubject(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-sm">Assign to Classes</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {classes.map((cls) => (
                        <label key={cls.code} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={subject.classes.includes(cls.code)}
                            onChange={(e) => {
                              const updated = [...subjects];
                              if (e.target.checked) {
                                updated[index].classes.push(cls.code);
                              } else {
                                updated[index].classes = updated[index].classes.filter(c => c !== cls.code);
                              }
                              setSubjects(updated);
                            }}
                            className="w-4 h-4 text-indigo-600"
                          />
                          {cls.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {step === 5 && (
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-indigo-900">Step 5: Institutions (Schools)</CardTitle>
                <Button onClick={addInstitution} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Institution
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {institutions.map((inst, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Institution Name</Label>
                    <Input
                      value={inst.name}
                      onChange={(e) => updateInstitution(index, 'name', e.target.value)}
                      placeholder="e.g., Delhi Public School"
                      className="border-slate-300"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Institution Code</Label>
                    <Input
                      value={inst.code}
                      onChange={(e) => updateInstitution(index, 'code', e.target.value)}
                      placeholder="e.g., DPS_001"
                      className="border-slate-300"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeInstitution(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between gap-4">
          {step > 1 && (
            <Button
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          {step < 5 ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              <Check className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Complete Setup'}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};
